<template>

    <transition-group
        tag="div"
        class="operations-banners"
        :css="false"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
    >
        <div
            v-for="operation in visibleOperations"
            :key="operation.key"
            class="operations-banner-slot"
        >
            <div
                class="operations-banner"
                :class="`operations-banner-${operation.status}`"
            >
                <div class="operations-banner-body">
                    <v-loader
                        v-if="operation.status === 'progress'"
                        size="xs"
                    />
                    <i
                        v-else-if="operation.status === 'failed'"
                        class="fas fa-exclamation-circle"
                        aria-hidden="true"
                    />
                    <i v-else class="fas fa-check-circle" aria-hidden="true" />
                    <span>{{ messageFor(operation) }}</span>
                </div>
                <v-close
                    v-if="operation.status === 'progress'"
                    class="operations-banner-close"
                    @click.native="dismiss(operation.key)"
                />
            </div>
        </div>
    </transition-group>

</template>

<style scoped lang="scss">

    .operations-banners {
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        position: sticky;
        top: 0;
        width: 100%;
        z-index: 202;
    }

    .operations-banner-slot {
        flex-shrink: 0;
        width: 100%;
    }

    .operations-banner {
        align-items: center;
        box-sizing: border-box;
        display: flex;
        justify-content: center;
        min-height: 40px;
        padding: 15px 40px;
        position: relative;
        text-align: center;
        transition: background-color .35s ease, color .35s ease;
        width: 100%;
        will-change: transform, opacity;
    }

    .operations-banner-body {
        @include font-medium(13px);
        align-items: center;
        display: inline-flex;
        gap: 8px;
        line-height: 1.4em;
        i {
            font-size: 14px;
        }
    }

    .operations-banner-close {
        color: inherit;
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
    }

    .operations-banner-failed {
        background-color: color-mix(in srgb, $color-danger 5%, $color-white);
        color: $color-danger;
    }

    .operations-banner-progress {
        background-color: color-mix(in srgb, $color-gold 14%, $color-white);
        color: color-mix(in srgb, $color-gold 45%, $color-text);
        :deep(.loader) {
            border-left-color: currentColor;
            border-top-color: currentColor;
        }
    }

    .operations-banner-success {
        background-color: color-mix(in srgb, $color-success 5%, $color-white);
        color: $color-success;
    }

</style>

<script>

import Close from '@cmp/Close';

const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const DURATION_MS = 400;

export default {

    components: {
        'v-close': Close,
    },

    data() {
        return {
            dismissTimers: {},
            resizeObserver: null,
        };
    },

    computed: {

        project() {
            return this.$store.getters.project;
        },

        operations() {
            return this.$store.getters.operations || [];
        },

        visibleOperations() {
            if (!this.project) {
                return [];
            }
            return this.operations.filter(o => o.projectId === this.project.id);
        },

    },

    watch: {

        operations: {
            deep: true,
            immediate: true,
            handler(operations) {
                (operations || []).forEach((operation) => {
                    if (!['success', 'failed'].includes(operation.status)) {
                        return;
                    }
                    if (this.dismissTimers[operation.key]) {
                        return;
                    }
                    this.dismissTimers[operation.key] = setTimeout(() => {
                        this.dismiss(operation.key);
                    }, operation.dismissAfter || 5000);
                });
            },
        },

    },

    mounted() {
        this.resizeObserver = new ResizeObserver(() => {
            this.publishOffset();
        });
        this.resizeObserver.observe(this.$el);
        this.publishOffset();
    },

    beforeDestroy() {
        Object.keys(this.dismissTimers).forEach((key) => {
            clearTimeout(this.dismissTimers[key]);
        });
        this.dismissTimers = {};
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        this.publishOffset(0);
    },

    methods: {

        publishOffset(height) {
            const primary = this.$el && this.$el.closest
                ? this.$el.closest('.primary')
                : null;
            if (!primary) {
                return;
            }
            const offset = height === 0
                ? 0
                : (this.$el ? this.$el.offsetHeight : 0);
            primary.style.setProperty('--operations-banner-offset', `${offset}px`);
        },

        messageFor(operation) {
            if (operation.status === 'failed' && operation.message) {
                return operation.message;
            }
            const name = operation.meta && operation.meta.integrationName
                ? operation.meta.integrationName
                : '';
            const suffix = {
                failed: 'failed',
                progress: 'progress',
                success: 'success',
            }[operation.status] || 'progress';
            return this.$t(`messages.operation_${operation.type}_${suffix}`, {
                integration_name: name,
            });
        },

        dismiss(key) {
            if (this.dismissTimers[key]) {
                clearTimeout(this.dismissTimers[key]);
                delete this.dismissTimers[key];
            }
            this.$store.commit('dismissOperation', key);
        },

        beforeEnter(el) {
            const banner = el.firstElementChild;
            el.style.height = '0';
            el.style.overflow = 'hidden';
            banner.style.opacity = '0';
            banner.style.transform = 'translateY(-100%)';
        },

        enter(el, done) {
            const banner = el.firstElementChild;
            const height = banner.scrollHeight;
            el.getBoundingClientRect();
            el.style.transition = `height ${DURATION_MS}ms ${EASE}`;
            banner.style.transition = [
                `opacity ${DURATION_MS}ms ease`,
                `transform ${DURATION_MS}ms ${EASE}`,
            ].join(', ');
            el.style.height = `${height}px`;
            banner.style.opacity = '1';
            banner.style.transform = 'translateY(0)';
            this._onTransitionEnd(el, 'height', done);
        },

        afterEnter(el) {
            const banner = el.firstElementChild;
            el.style.height = '';
            el.style.overflow = '';
            el.style.transition = '';
            banner.style.opacity = '';
            banner.style.transform = '';
            banner.style.transition = '';
        },

        beforeLeave(el) {
            el.style.height = `${el.offsetHeight}px`;
            el.style.overflow = 'hidden';
        },

        leave(el, done) {
            const banner = el.firstElementChild;
            el.getBoundingClientRect();
            el.style.transition = `height ${DURATION_MS}ms ${EASE}`;
            banner.style.transition = [
                `opacity ${DURATION_MS * 0.75}ms ease`,
                `transform ${DURATION_MS}ms ${EASE}`,
            ].join(', ');
            el.style.height = '0';
            banner.style.opacity = '0';
            banner.style.transform = 'translateY(-100%)';
            this._onTransitionEnd(el, 'height', done);
        },

        _onTransitionEnd(el, property, done) {
            let finished = false;
            const finish = () => {
                if (finished) {
                    return;
                }
                finished = true;
                el.removeEventListener('transitionend', onEnd);
                clearTimeout(fallback);
                done();
            };
            const onEnd = (event) => {
                if (event.target !== el || event.propertyName !== property) {
                    return;
                }
                finish();
            };
            el.addEventListener('transitionend', onEnd);
            const fallback = setTimeout(finish, DURATION_MS + 50);
        },

    },

}

</script>
